import React, { useState, useEffect } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import axios from 'axios';
import { Button } from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import apiClient from "../../../pages/keycloak/axiosInstance";

const Analysis = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalProjectDamaged, setTotalProjectDamaged] = useState(0);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const projectId = localStorage.getItem('selectedProject') || '';
        const supplierName = sessionStorage.getItem('selectedSupplierTab') || '';

        const response = await apiClient.post(`vehicle_analysis`, {
          projectId,
          supplierName
        });
        setTotalProjectDamaged(response.data.totalProjectDamaged || 0);
        // Access the nested data array and ensure it's an array
        const responseData = Array.isArray(response.data.data) ? response.data.data : [];
        // Map API response to match expected field names
        const mappedData = responseData.map((item, index) => ({
          id: index + 1, // Generate Sr.no
          date: item.dateOfReceiving,
          vehicleNo: item.vehicleNumber,
          orderNo: item.orderNo || '-', // Add fallback if not provided
          invoiceNo: item.invoiceNo || '-', // Add fallback if not provided
          noOfPallet: item.totalNumberOfPallets,
          noOfModule: item.totalNumberOfModules,
          damaged: item.damaged,
          damagedQuantity: item.damaged, // Map to API's damaged field
          damagedImage: item.damagedImagesZipUrl || '',
          shortQuantity: item.short,
          rejectedQuantity: item.rejected,
          excessQuantity: item.excess || 0, // Add fallback if not provided
          acceptedQuantity: item.accepted,
          receivedQuantity: item.totalReceived || 'N/A', // Assuming accepted is received
          remarks: item.remarks || '-',
          documents: item.documentsZipUrl || '',
          palletImagesZip: item.palletImagesZipUrl || ''
        }));
        setData(mappedData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch data');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter data based on search term and date range
  const filteredData = data.filter((item) => {
    if (!item || !item.date) return false; // Guard against null/undefined items
    const itemDate = dayjs(item.date);
    const [startDate, endDate] = dateRange;
    const isDateInRange =
      (!startDate || itemDate.isAfter(dayjs(startDate).subtract(1, 'day'))) &&
      (!endDate || itemDate.isBefore(dayjs(endDate).add(1, 'day')));
    const searchMatch = Object.values(item).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );
    return isDateInRange && searchMatch;
  });

  // Calculate totals
  const totalDamagedQuantity = data.reduce(
    (sum, item) => sum + (item.damagedQuantity || 0),
    0
  );
  const totalModulesReceived = data.reduce(
    (sum, item) => sum + (item.receivedQuantity || 0),
    0
  );
  const totalModulesDamaged = totalDamagedQuantity;
  const totalShortQuantity = data.reduce(
    (sum, item) => sum + (item.shortQuantity || 0),
    0
  );
  const totalAcceptedQuantity = data.reduce(
    (sum, item) => sum + (item.acceptedQuantity || 0),
    0
  );
  const totalRejectedQuantity = data.reduce(
    (sum, item) => sum + (item.rejectedQuantity || 0),
    0
  );

  const exportToExcel = () => {
    const exportData = filteredData.map(item => ({
      "Sr.no": item.id,
      "Date": item.date || "-",
      "Vehicle No": item.vehicleNo || "-",
      "Order No": item.orderNo || "-",
      "Invoice No": item.invoiceNo || "-",
      "No of Pallet": item.noOfPallet || 0,
      "No of Module": item.noOfModule || 0,
      "Damaged Quantity (Modules)": item.damagedQuantity || 0,
      "Damaged Image": item.damagedImage ? `=HYPERLINK("${item.damagedImage}", "DOWNLOAD")` : "-",
      "Short Quantity": item.shortQuantity || 0,
      "Rejected Quantity": item.rejectedQuantity || 0,
      "Excess Quantity": item.excessQuantity || 0,
      "Accepted Quantity": item.acceptedQuantity || 0,
      "Received Quantity": item.receivedQuantity || 0,
      "Remarks": item.remarks || "-",
      "Documents": item.documents ? `=HYPERLINK("${item.documents}", "DOWNLOAD")` : "-",
      "Vehicle inspection images": item.palletImagesZip ? `=HYPERLINK("${item.palletImagesZip}", "DOWNLOAD")` : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    ws["!cols"] = [
      { wch: 8 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 12 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 30 },
      { wch: 25 }, { wch: 30 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicle Analysis");

    const projectId = localStorage.getItem('selectedProject') || 'Unknown';
    const fileName = `Vehicle_Analysis_${projectId}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div className="p-2 bg-gray-100">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border text-center border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
            Total Damaged Quantity (Project Wise)
          </h3>
          <p className="text-xl sm:text-2xl font-bold text-[#005A9C]">
            {totalProjectDamaged} Modules
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border text-center border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
            Total Modules Received
          </h3>
          <p className="text-xl sm:text-2xl font-bold text-[#005A9C]">
            {totalModulesReceived} Modules
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border text-center border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
            Total Modules Damaged
          </h3>
          <p className="text-xl sm:text-2xl font-bold text-[#005A9C]">
            {totalModulesDamaged} Modules
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border text-center border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
            Total Short Quantity
          </h3>
          <p className="text-xl sm:text-2xl font-bold text-[#005A9C]">
            {totalShortQuantity} Modules
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border text-center border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
            Total Accepted Quantity
          </h3>
          <p className="text-xl sm:text-2xl font-bold text-[#005A9C]">
            {totalAcceptedQuantity} Modules
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border text-center border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
            Total Rejected Quantity
          </h3>
          <p className="text-xl sm:text-2xl font-bold text-[#005A9C]">
            {totalRejectedQuantity} Modules
          </p>
        </div>
      </div>

      {/* Date Range and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
        <div className="flex-1 w-full sm:w-auto">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={['DateRangePicker']}>
              <DateRangePicker
                value={dateRange}
                onChange={(newValue) => setDateRange(newValue)}
                className="w-full bg-white rounded-lg"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { '& .MuiInputBase-root': { height: '42px' } },
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
        </div>
        <div className="flex-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search..."
            className="w-full p-3 mt-1 h-[42px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 transition text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button
          variant="contained"
          onClick={exportToExcel}
          disabled={loading || filteredData.length === 0}
          sx={{
            height: 40,
            px: 4,
            fontWeight: 600,
            backgroundColor: "#424752",
            color: "white",
            "&:hover": {
              backgroundColor: "#222222", // Slightly lighter on hover
            },
            "&:disabled": {
              backgroundColor: "#cccccc",
              color: "#666666",
            },
          }}
        >
          Download as Excel
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-600 sticky top-0">
              <tr>
                {[
                  'Sr.no',
                  'Date',
                  'Vehicle No',
                  'Order No',
                  'Invoice No',
                  'No of Pallet',
                  'No of Module',
                  'Damaged Quantity (Modules)',
                  'Damaged Image',
                  'Short Quantity',
                  'Rejected Quantity',
                  'Excess Quantity',
                  'Accepted Quantity',
                  'Received Quantity',
                  'Remarks',
                  'Documents',
                  'Vehicle inspection images'
                ].map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={17} className="px-4 py-3 text-center text-sm text-gray-600">
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={17} className="px-4 py-3 text-center text-sm text-red-600">
                    {error}
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan={17} className="px-4 py-3 text-center text-sm text-gray-600">
                    No data found
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.id}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.date}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.vehicleNo}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.orderNo}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.invoiceNo}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.noOfPallet}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.noOfModule}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.damagedQuantity}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm">
                      {item.damagedImage ? (
                        <a
                          href={item.damagedImage}
                          className="text-blue-600 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          LINK
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.shortQuantity}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.rejectedQuantity}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.excessQuantity}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.acceptedQuantity}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.receivedQuantity}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-600">{item.remarks}</td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm">
                      {item.documents ? (
                        <a
                          href={item.documents}
                          className="text-blue-600 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          LINK
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap text-sm">
                      {item.palletImagesZip ? (
                        <a
                         href={item.palletImagesZip}
                         className="text-blue-600 hover:underline"
                         target="_blank"
                         rel="noopener noreferrer"
                        >
                          LINK
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analysis;